import { useEffect, useState, type ReactNode } from 'react'
import { ApiError } from '../api/client'

/** Money, always with two decimals and tabular figures so columns line up. */
export function money(value: number | null | undefined, blankZero = false): string {
  if (value === null || value === undefined) return ''
  if (blankZero && value === 0) return ''

  const text = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  // Accounting convention: negatives in brackets, not with a minus sign.
  return value < 0 ? `(${text})` : text
}

export function Money({ value, blankZero = false }: { value: number | null | undefined; blankZero?: boolean }) {
  const negative = (value ?? 0) < 0
  return <span className={negative ? 'negative' : undefined}>{money(value, blankZero)}</span>
}

/** dd/MM/yyyy — how the office writes dates. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('en-GB')
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}

/** ISO yyyy-MM-dd, which is what the API's DateOnly parameters expect. */
export function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10)
}

export function startOfMonth(reference = new Date()): string {
  return isoDate(new Date(reference.getFullYear(), reference.getMonth(), 1))
}

export function endOfMonth(reference = new Date()): string {
  return isoDate(new Date(reference.getFullYear(), reference.getMonth() + 1, 0))
}

export function Panel({
  title,
  actions,
  flush = false,
  children,
}: {
  title?: ReactNode
  actions?: ReactNode
  flush?: boolean
  children: ReactNode
}) {
  return (
    <section className="panel">
      {(title || actions) && (
        <header className="panel-head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions}
        </header>
      )}
      <div className={flush ? 'panel-body flush' : 'panel-body'}>{children}</div>
    </section>
  )
}

/**
 * Renders an API failure. Domain errors carry per-line detail, which is exactly what a rejected
 * posting needs to show — "line 3 posts to a group account" beats "validation failed".
 */
export function ErrorBanner({ error, onDismiss }: { error: unknown; onDismiss?: () => void }) {
  if (!error) return null

  const apiError = error instanceof ApiError ? error : null
  const message = error instanceof Error ? error.message : String(error)
  const details = apiError?.details ?? []

  return (
    <div className="banner error">
      <div style={{ flex: 1 }}>
        <strong>{message}</strong>
        {details.length > 1 && (
          <ul>
            {details.map((detail, index) => (
              <li key={index}>{detail}</li>
            ))}
          </ul>
        )}
        {apiError?.correlationId && (
          <div className="muted" style={{ marginTop: 6, fontSize: 11 }}>
            Reference {apiError.correlationId}
          </div>
        )}
      </div>
      {onDismiss && (
        <button className="subtle" onClick={onDismiss}>
          Dismiss
        </button>
      )}
    </div>
  )
}

export function Modal({
  title,
  onClose,
  footer,
  width = 'default',
  children,
}: {
  title: string
  onClose: () => void
  footer?: ReactNode
  width?: 'default' | 'wide' | 'narrow'
  children: ReactNode
}) {
  // Escape closes. A modal that traps you is worse than no modal.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={width === 'default' ? 'modal' : `modal ${width}`}>
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="subtle" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  )
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && (
        <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
          {hint}
        </div>
      )}
    </div>
  )
}

export function Tag({ kind = 'default', children }: { kind?: string; children: ReactNode }) {
  return <span className={kind === 'default' ? 'tag' : `tag ${kind}`}>{children}</span>
}

export function StatusTag({ status }: { status: string }) {
  const kind =
    status === 'Posted' || status === 'Open' || status === 'Approved'
      ? 'ok'
      : status === 'Draft' || status === 'Logged' || status === 'PendingReview' || status === 'Reviewed'
        ? 'warn'
        : status === 'Reversed' || status === 'Rejected' || status === 'Voided' || status === 'Locked'
          ? 'danger'
          : 'info'

  return <Tag kind={kind}>{status}</Tag>
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <div className="loading">{what}…</div>
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

/** A date range with sensible defaults — most reports are run for the current month. */
export function useDateRange(initialFrom = startOfMonth(), initialTo = endOfMonth()) {
  const [from, setFrom] = useState(initialFrom)
  const [to, setTo] = useState(initialTo)
  return { from, to, setFrom, setTo }
}
